import {nextTick} from "vue";
import {useWorkspace} from "../Stores/Workspaces";
import {useAppState} from "../Stores/AppState";
import {useWorkspaceTree} from "../Stores/WorkspaceTree";
import {
    endEntityLocatorNavTrace,
    getActiveEntityLocatorNavTraceId,
    logEntityLocatorNav,
} from "./EntityLocatorNavDebug";
import {
    resolveDivisionIdFromSpaceGroupId,
    resolveDivisionIdFromSpaceId,
} from "./workspaceDivisionFilter";
import { clearDestinationItemPanelFiltersForLocate } from "../Components/WorkPanel/Composables/useItemPanelFilters";
import {
    getActiveLocateHighlightArmedState,
    isEntityLocatorTargetInDom,
    shouldClearArmedLocateTemp,
} from "./activeLocateHighlight";

/**
 * LocationAnimation utility class for handling location-based animations
 * and scrolling to specific elements in the workspace
 */
export class LocationAnimation {
    constructor() {
        this.workspaceStore = useWorkspace();
        this.workspaceTreeStore = useWorkspaceTree();
        this.appStateStore = useAppState();
    }

    shouldPlayEntityLocatorHighlight(context = "unknown") {
        const app = this.appStateStore;
        const shouldPlay =
            app.entity_navigate_with_locator ||
            !app.suppress_entity_locator_after_document_reload;

        if (getActiveEntityLocatorNavTraceId()) {
            logEntityLocatorNav("locationAnimation.shouldPlayHighlight", {
                context,
                shouldPlay,
            });
        }

        return shouldPlay;
    }

    /** Absolute retry times (ms from retry start) when the target row is not mounted yet. */
    static LOCATE_SCROLL_RETRY_DELAYS = [0, 300, 600, 900, 1200, 1500];

    scheduleScrollAndHighlight(getElement, onScroll) {
        const delays = LocationAnimation.LOCATE_SCROLL_RETRY_DELAYS;
        // Capture ownership at start so a late give-up cannot wipe a newer locate.
        const expected_locator_class =
            this.workspaceStore.active_item_locator_temp;

        return new Promise((resolve) => {
            const started_at = performance.now();

            const tryScroll = (attempt) => {
                const element = getElement();

                if (element) {
                    onScroll?.(element);
                    if (this.shouldPlayEntityLocatorHighlight()) {
                        this.setActiveItemLocator();
                    } else {
                        this.clearArmedLocateTemp(expected_locator_class);
                    }
                    resolve();
                    return;
                }

                const next_attempt = attempt + 1;

                if (next_attempt < delays.length) {
                    const wait_ms = Math.max(
                        0,
                        delays[next_attempt] - (performance.now() - started_at),
                    );
                    setTimeout(() => tryScroll(next_attempt), wait_ms);
                    return;
                }

                // Target never mounted — do not leave *this* locate's temp armed.
                this.clearArmedLocateTemp(expected_locator_class);
                resolve();
            };

            nextTick(() => tryScroll(0));
        });
    }

    /**
     * Drop a locate that never promoted so a later setActiveItemLocator cannot reuse it.
     * When expected_locator_class is passed, clear only if this attempt still owns temp
     * (avoids stale retries wiping a newer arm/promote).
     *
     * @param {string|null|undefined} [expected_locator_class]
     */
    clearArmedLocateTemp(expected_locator_class) {
        if (
            arguments.length > 0 &&
            !shouldClearArmedLocateTemp(
                this.workspaceStore.active_item_locator_temp,
                expected_locator_class,
            )
        ) {
            return;
        }

        this.workspaceStore.active_item_locator_temp = null;
        this.workspaceStore.show_work_rail_locator = false;
    }

    /**
     * Sets the locate class name for animation targeting
     * @param {string} type - The type of element (space, board, item, group)
     * @param {Object} item - The item object to locate
     */
    setLocateClassName(type, item) {
        if (!this.shouldPlayEntityLocatorHighlight(`setLocateClassName:${type}`)) {
            if (getActiveEntityLocatorNavTraceId()) {
                logEntityLocatorNav("locationAnimation.setLocateClassName.skipped", {
                    type,
                    itemId: item?.id,
                });
            }
            return;
        }

        const resolvedItem = (() => {
            switch (type) {
                case "space":
                    return item || this.workspaceTreeStore.activeWorkspace;
                case "board":
                    return item || this.workspaceTreeStore.activeBoard;
                case "item":
                    return item || this.workspaceTreeStore.activeItem;
                case "group":
                    return item; // For groups, item is already the group object
                default:
                    return item;
            }
        })();

        Object.assign(
            this.workspaceStore,
            getActiveLocateHighlightArmedState(
                type,
                `auto-scroll-${resolvedItem?.id}${resolvedItem?.slug}`,
            ),
        );
    }

    /**
     * Redirects to active item with scroll animation
     */
    redirectToActiveItem(locateItemOverride = null) {
        const activeItem = this.workspaceTreeStore.activeItem;
        const scrollItem = locateItemOverride ?? activeItem;
        const elIdFromTemp = () => this.workspaceStore.active_item_locator_temp;

        if (scrollItem == null && !elIdFromTemp()) {
            return Promise.resolve();
        }

        const resolveElId = () =>
            scrollItem != null
                ? `auto-scroll-${scrollItem.id}${scrollItem.slug}`
                : elIdFromTemp();

        return this.scheduleScrollAndHighlight(
            () => {
                const resolvedElId = resolveElId();
                if (!resolvedElId) {
                    return null;
                }
                return (
                    document?.getElementsByClassName(resolvedElId)?.[0] ?? null
                );
            },
            (element) => {
                if (!this.workspaceStore.activeItemSavedScrollPosition) {
                    element.scrollIntoView?.({
                        block: "end",
                    });
                } else {
                    const categoryElement = element.closest(
                        ".propus-items-container",
                    );
                    const categoriesElement = element.closest(
                        ".prospus-draggable-categories",
                    );
                    const {item, category} =
                        this.workspaceStore.activeItemSavedScrollPosition;
                    categoriesElement?.scrollBy(category.x, category.y);
                    categoryElement?.scrollBy(item.x, item.y);
                    this.workspaceStore.activeItemSavedScrollPosition = null;
                }
            },
        );
    }

    /**
     * Redirects to active space with scroll animation
     */
    redirectToActiveSpace() {
        const activeSpace = this.workspaceTreeStore.activeWorkspace;
        if (!activeSpace) {
            return Promise.resolve();
        }

        const elId = "id-" + activeSpace?.id + activeSpace?.slug;

        if (!elId) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            nextTick(() => {
                const ws = this.workspaceTreeStore.getWorkspaceById(
                    activeSpace.id,
                );
                const activeSpaceColumn = ws
                    ? this.workspaceTreeStore.getWorkspaceCategoryById(
                        ws.workspaceCategoryId,
                    )
                    : null;
                const scrollContainer = document.querySelector(
                    "#prospus-workpanel-spaces-container",
                );

                if (scrollContainer) {
                    let activeSpaceOrder = activeSpaceColumn?.order;
                    if (
                        activeSpaceOrder === null ||
                        activeSpaceOrder === undefined
                    ) {
                        const catId = ws?.workspaceCategoryId;
                        activeSpaceOrder =
                            catId != null
                                ? this.workspaceTreeStore.workspaceCategoryOrderedIds.indexOf(
                                    catId,
                                )
                                : -1;
                    }

                    const totalColumns = scrollContainer.children.length;
                    const containerWidth = scrollContainer.offsetWidth;
                    const columnWidth = scrollContainer.children[0].offsetWidth;
                    const availableColumns = totalColumns - activeSpaceOrder;

                    const visibleColumnsCount = Math.trunc(
                        containerWidth / columnWidth,
                    );
                    const isSpaceOrderLessThanVisibleColumns =
                        activeSpaceOrder < visibleColumnsCount;

                    const isSpaceOrderLessThanAvailableColumns =
                        activeSpaceOrder < availableColumns;

                    const isSpaceVisible =
                        containerWidth > availableColumns * columnWidth * 0.5;

                    const canBeShownNearest =
                        isSpaceVisible ||
                        isSpaceOrderLessThanVisibleColumns ||
                        isSpaceOrderLessThanAvailableColumns;

                    document.getElementById(elId)?.scrollIntoView?.({
                        block: "end",
                        inline: canBeShownNearest ? "nearest" : "start",
                    });
                }

                const locate_class = `auto-scroll-${activeSpace.id}${activeSpace.slug}`;
                this.redirectToActive(locate_class).then(() => resolve());
            });
        });
    }

    /**
     * Redirects to active board with scroll animation
     */
    redirectToActiveBoard() {
        const activeBoard = this.workspaceTreeStore.activeBoard;
        if (!activeBoard) {
            if (getActiveEntityLocatorNavTraceId()) {
                logEntityLocatorNav("locationAnimation.redirectToActiveBoard.skipped", {
                    reason: "no_active_board",
                });
            }
            return Promise.resolve();
        }
        const elId = `auto-scroll-${activeBoard?.id}${activeBoard?.slug}`;

        if (getActiveEntityLocatorNavTraceId()) {
            logEntityLocatorNav("locationAnimation.redirectToActiveBoard.start", {
                boardId: activeBoard.id,
                elId,
            });
        }

        return this.redirectToActive(elId, {retry: true});
    }

    /**
     * Redirects to active board group with scroll animation
     */
    redirectToActiveBoardGroup() {
        const activeBoardCategory = this.workspaceTreeStore.activeBoardCategory;
        if (!activeBoardCategory) {
            return Promise.resolve();
        }
        this.workspaceStore.openBoardsPanel();
        const elId = `auto-scroll-${activeBoardCategory?.id}${activeBoardCategory?.slug}`;
        return this.redirectToActive(elId, {retry: true});
    }

    /**
     * Redirects to active space group with scroll animation
     */
    redirectToActiveSpaceGroup() {
        const activeSpaceCategory =
            this.workspaceTreeStore.activeWorkspaceCategory;
        if (!activeSpaceCategory) {
            return Promise.resolve();
        }
        this.workspaceStore.openSpacesPanel();
        const elId = `auto-scroll-${activeSpaceCategory?.id}${activeSpaceCategory?.slug}`;
        return this.redirectToActive(elId, {retry: true});
    }

    /**
     * Generic redirect to active element with scroll animation
     * @param {string} id - The element ID to scroll to
     */
    redirectToActive(id = this.workspaceStore.active_item_locator_temp) {
        const delays = LocationAnimation.LOCATE_SCROLL_RETRY_DELAYS;
        // Ownership for this redirect — late give-up must not clear a newer locate.
        const expected_locator_class = id;

        return new Promise((resolve) => {
            const started_at = performance.now();

            const tryScroll = (attempt) => {
                if (!id) {
                    if (getActiveEntityLocatorNavTraceId()) {
                        logEntityLocatorNav("locationAnimation.redirectToActive.skipped", {
                            reason: "missing_element_id",
                        });
                    }
                    resolve();
                    return;
                }

                const elem = document.querySelector(`.${id}`);

                if (elem) {
                    elem.scrollIntoView?.({block: "end"});
                    const shouldHighlight = this.shouldPlayEntityLocatorHighlight(
                        `redirectToActive:found:${id}`,
                    );
                    if (shouldHighlight) {
                        this.setActiveItemLocator();
                    } else {
                        this.clearArmedLocateTemp(expected_locator_class);
                    }
                    if (getActiveEntityLocatorNavTraceId()) {
                        logEntityLocatorNav("locationAnimation.redirectToActive.found", {
                            elId: id,
                            attempt,
                            shouldHighlight,
                        });
                    }
                    resolve();
                    return;
                }

                const next_attempt = attempt + 1;

                if (next_attempt < delays.length) {
                    const wait_ms = Math.max(
                        0,
                        delays[next_attempt] - (performance.now() - started_at),
                    );
                    if (getActiveEntityLocatorNavTraceId()) {
                        logEntityLocatorNav("locationAnimation.redirectToActive.retry", {
                            elId: id,
                            attempt,
                            waitMs: wait_ms,
                            atMs: delays[next_attempt],
                        });
                    }
                    setTimeout(() => tryScroll(next_attempt), wait_ms);
                    return;
                }

                if (getActiveEntityLocatorNavTraceId()) {
                    logEntityLocatorNav("locationAnimation.redirectToActive.gave_up", {
                        elId: id,
                        attempts: attempt + 1,
                    });
                }
                // Missed locate must not leave *this* temp armed for a later promote.
                this.clearArmedLocateTemp(expected_locator_class);
                resolve();
            };

            nextTick(() => tryScroll(0));
        });
    }


    /**
     * Sets the active item locator with timeout.
     * Only promotes when the `.auto-scroll-*` activator exists — otherwise
     * Vuetify parks the tooltip at top-left (near AppBar / logo).
     */
    setActiveItemLocator() {
        if (!this.shouldPlayEntityLocatorHighlight("setActiveItemLocator")) {
            if (getActiveEntityLocatorNavTraceId()) {
                logEntityLocatorNav("locationAnimation.setActiveItemLocator.skipped", {
                    locatorTemp: this.workspaceStore.active_item_locator_temp,
                });
            }
            return;
        }
        setTimeout(() => {
            const locator_class = this.workspaceStore.active_item_locator_temp;

            if (
                this.workspaceStore.active_item_locator_cancel ||
                !locator_class
            ) {
                return;
            }

            const delays = LocationAnimation.LOCATE_SCROLL_RETRY_DELAYS;
            const started_at = performance.now();

            const tryPromote = (attempt) => {
                if (
                    this.workspaceStore.active_item_locator_cancel ||
                    this.workspaceStore.active_item_locator_temp !== locator_class
                ) {
                    return;
                }

                if (isEntityLocatorTargetInDom(locator_class)) {
                    this.workspaceStore.active_item_locator = locator_class;
                    this.workspaceStore.active_item_locator_temp = null;
                    this.workspaceStore.show_work_rail_locator = true;

                    if (getActiveEntityLocatorNavTraceId()) {
                        logEntityLocatorNav(
                            "locationAnimation.setActiveItemLocator.promoted",
                            {
                                locator: locator_class,
                                attempt,
                            },
                        );
                    }
                    return;
                }

                const next_attempt = attempt + 1;

                if (next_attempt < delays.length) {
                    const wait_ms = Math.max(
                        0,
                        delays[next_attempt] - (performance.now() - started_at),
                    );
                    if (getActiveEntityLocatorNavTraceId()) {
                        logEntityLocatorNav(
                            "locationAnimation.setActiveItemLocator.retry",
                            {
                                locator: locator_class,
                                attempt,
                                waitMs: wait_ms,
                                atMs: delays[next_attempt],
                            },
                        );
                    }
                    setTimeout(() => tryPromote(next_attempt), wait_ms);
                    return;
                }

                if (getActiveEntityLocatorNavTraceId()) {
                    logEntityLocatorNav(
                        "locationAnimation.setActiveItemLocator.gave_up",
                        {
                            locator: locator_class,
                            attempts: attempt + 1,
                        },
                    );
                }
                this.workspaceStore.active_item_locator_temp = null;
                this.workspaceStore.show_work_rail_locator = false;
            };

            tryPromote(0);
        }, 1200);
    }

    /**
     * Checks if an element is in the viewport
     * @param {HTMLElement} element - The element to check
     * @returns {boolean} - True if element is in viewport
     */
    isElementInViewport(element) {
        const rect = element.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <=
            (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    /**
     * Scrolls to a specific element by ID with animation
     * @param {string} elementId - The ID of the element to scroll to
     * @param {Object} options - Scroll options (block, inline, etc.)
     */
    scrollToElement(elementId, options = {block: "end"}) {
        const element = document.getElementById(elementId);
        if (element) {
            element.scrollIntoView?.(options);
        }
    }

    /**
     * Scrolls to a specific element by class name with animation
     * @param {string} className - The class name of the element to scroll to
     * @param {Object} options - Scroll options (block, inline, etc.)
     */
    scrollToElementByClass(className, options = {block: "end"}) {
        const element = document.querySelector(`.${className}`);
        if (element) {
            element.scrollIntoView?.(options);
        }
    }

    /**
     * When locating/navigating, include the Space Group's division in the
     * active division filter so the Spaces subpanel can show the target.
     */
    ensureDivisionFilterForNavigation({
        spaceId = null,
        groupId = null,
        itemId = null,
        boardId = null,
    } = {}) {

        let resolvedSpaceId = spaceId;

        if (!resolvedSpaceId && !groupId && itemId) {
            const item = this.workspaceTreeStore.getItemById(itemId);
            resolvedSpaceId = item?.workspaceId ?? item?.workspace_id;
        }

        if (!resolvedSpaceId && !groupId && boardId) {
            const board = this.workspaceTreeStore.getBoardById(boardId);
            resolvedSpaceId = board?.workspaceId ?? board?.workspace_id;
        }
        const hasTarget = Boolean(groupId || resolvedSpaceId);
        const divisionId = groupId
            ? resolveDivisionIdFromSpaceGroupId(groupId)
            : resolveDivisionIdFromSpaceId(resolvedSpaceId);

        if (divisionId) {
            this.appStateStore.ensureWorkspaceFilterIncludes(divisionId);
            return;
        }

        if (hasTarget) {
            this.appStateStore.ensureWorkspaceFilterIncludesUnmapped();
        }
    }

    /**
     * Navigates to an item with full business logic
     * @param {number} itemId - The item ID to navigate to
     * @param {number} spaceId - The space ID containing the item
     * @param {Object} itemData - The item data object for animation
     * @param {Function} openConversationMappedItem - Optional callback for opening conversation mapped item
     */
    async navigateToItem(
        itemId,
        spaceId,
        itemData = null,
        openConversationMappedItem = null,
        boardId = null,
    ) {
        this.ensureDivisionFilterForNavigation({spaceId, itemId, boardId});

        if (openConversationMappedItem) {
            openConversationMappedItem();
            return;
        }

        this.appStateStore.is_dashboard_mode = false;
        this.workspaceStore.setIsFetchingItem(true);

        if (itemId != null) {
            this.workspaceTreeStore.setActiveItemId(itemId, false);
        }

        const space = this.workspaceTreeStore.getWorkspaceById(spaceId);
        if (!space?.boardCategoryOrderedIds?.length) {
            await this.workspaceTreeStore.loadBoards(space);
        }
        this.workspaceTreeStore.setActiveWorkspaceId(space?.id, false);

        const board = this.workspaceTreeStore.getBoardById(boardId);
        if (!board?.itemCategoryOrderedIds?.length) {
            await this.workspaceTreeStore.loadItems(space, board);
        }
        this.workspaceTreeStore.setActiveBoardId(board?.id, true);
        let itemRow =
            this.workspaceTreeStore.getItemById(itemId) ??
            this.workspaceTreeStore.getItemById(Number(itemId));

        if (!itemRow && space && board) {
            await this.workspaceTreeStore.loadItems(space, board, true);
            itemRow =
                this.workspaceTreeStore.getItemById(itemId) ??
                this.workspaceTreeStore.getItemById(Number(itemId));
        }

        if (this.workspaceStore.active_panel !== "items") {
            await new Promise((resolve) => setTimeout(resolve, 1));
            this.workspaceStore.openItemsPanel();
        }
        await nextTick();

        const categoryIdFromPayload =
            itemData?.item_category_id ?? itemData?.itemCategoryId;
        const boardIdResolved =
            itemRow?.boardId ??
            itemData?.board_id ??
            itemData?.boardId ??
            boardId;
        const workspaceIdResolved =
            itemRow?.workspaceId ??
            itemData?.workspace_id ??
            itemData?.workspaceId ??
            spaceId;
        const categoryIdResolved =
            itemRow?.itemCategoryId ?? categoryIdFromPayload;

        if (workspaceIdResolved) {
            this.workspaceTreeStore.setActiveWorkspaceId(
                workspaceIdResolved,
                true,
            );
        }
        if (boardIdResolved) {
            this.workspaceTreeStore.setActiveBoardId(boardIdResolved, true);
        }
        if (itemId != null) {
            this.workspaceTreeStore.setActiveItemCategoryId(null);
            this.workspaceTreeStore.setActiveItemId(itemId, true);
        } else if (categoryIdResolved != null) {
            this.workspaceTreeStore.setActiveItemCategoryId(
                categoryIdResolved,
                true,
            );
        }

        if (itemRow && itemData?.slug != null) {
            this.workspaceTreeStore.syncItemById(itemRow.id, {
                slug: itemData.slug,
            });
        }

        const categoryEntity =
            categoryIdResolved != null
                ? this.workspaceTreeStore.getItemCategoryById(
                    categoryIdResolved,
                )
                : null;
        if (categoryEntity?.itemOrderedIds && itemId != null) {
            const hasId = categoryEntity.itemOrderedIds.some(
                (id) => id == itemId,
            );
            if (!hasId) {
                categoryEntity.itemOrderedIds.push(itemId);
            }
        }

        const itemToActivate = itemRow ?? itemData;
        if (itemToActivate?.id) {
            this.workspaceStore.setActiveItem(itemToActivate, false);
        }

        if (itemData) {
            this.setLocateClassName("item", itemData);
        }

        // Destination-board Work Filters can hide the located item. Clear them
        // after the active board is set (so persist targets the destination)
        // and before scroll/highlight so the row can mount.
        // clearItemPanelFiltersForLocate(
        //     this.workspaceStore.item_panel_filters,
        //     () => this.workspaceStore.clearItemPanelFilters(),
        // );

        // Destination-board Work Filters can hide the located item. Load that
        // board's filters first (watcher only runs on nextTick), then clear
        // only when the item is not already included in the filtered results.
        clearDestinationItemPanelFiltersForLocate({
            boardId: boardIdResolved,
            loadFiltersForBoard: (board_id) =>
                this.workspaceStore.loadItemPanelFiltersForBoard(board_id),
            getFilters: () => this.workspaceStore.item_panel_filters,
            clearFilters: () => this.workspaceStore.clearItemPanelFilters(),
            item: itemRow ?? itemData,
        });
        await nextTick();

        await this.redirectToActiveItem(itemData);
        this.workspaceStore.setIsFetchingItem(false);

        if (getActiveEntityLocatorNavTraceId()) {
            endEntityLocatorNavTrace({
                status: "success",
                entityType: "item",
                itemId,
            });
        }
    }

    /**
     * Navigates to a board with full business logic
     * @param {number} boardId - The board ID to navigate to
     * @param {number} spaceId - The space ID containing the board
     * @param {Object} boardData - The board data object for animation
     */
    async navigateToBoard(boardId, spaceId, boardData = null) {
        this.ensureDivisionFilterForNavigation({spaceId, boardId});

        if (getActiveEntityLocatorNavTraceId()) {
            logEntityLocatorNav("locationAnimation.navigateToBoard.start", {
                boardId,
                spaceId,
                hasBoardData: Boolean(boardData),
                activePanel: this.workspaceStore.active_panel,
            });
        }

        this.appStateStore.is_dashboard_mode = false;
        // Routing handled by EntityNavigator or calling context
        this.workspaceStore.setIsFetchingItem(true);

        const space = this.workspaceTreeStore.getWorkspaceById(spaceId);
        this.workspaceTreeStore.setActiveWorkspaceId(space?.id, false);

        try {
            this.appStateStore.startRestoringState();
            await this.workspaceTreeStore.loadBoards(space);
            const board = this.workspaceTreeStore.getBoardById(boardId);
            this.workspaceTreeStore.setActiveBoardId(board?.id, true);

            if (getActiveEntityLocatorNavTraceId()) {
                logEntityLocatorNav("locationAnimation.navigateToBoard.boardResolved", {
                    boardId,
                    resolvedBoardId: board?.id,
                    boardSlug: board?.slug,
                    spaceId,
                });
            }

            if (this.workspaceStore.active_panel !== "boards") {
                await new Promise((resolve) => setTimeout(resolve, 1));
                this.workspaceStore.openBoardsPanel();
            }
            await nextTick();

            if (boardData) {
                await this.handleLocationAnimation("board", boardData);
            } else {
                await this.handleLocationAnimation("board", board);
            }

            if (getActiveEntityLocatorNavTraceId()) {
                logEntityLocatorNav("locationAnimation.navigateToBoard.done", {
                    boardId: board?.id,
                    locatorTemp: this.workspaceStore.active_item_locator_temp,
                    locator: this.workspaceStore.active_item_locator,
                });
                endEntityLocatorNavTrace({
                    status: "success",
                    entityType: "board",
                    boardId: board?.id,
                });
            }

            this.workspaceStore.setIsFetchingItem(false);
            this.appStateStore.stopRestoringState();
        } catch (error) {
            if (getActiveEntityLocatorNavTraceId()) {
                endEntityLocatorNavTrace({
                    status: "error",
                    entityType: "board",
                    message: error?.message,
                });
            }
            this.workspaceStore.setIsFetchingItem(false);
            this.appStateStore.stopRestoringState();
        }
    }

    /**
     * Navigates to a space with full business logic
     * @param {number} spaceId - The space ID to navigate to
     * @param {Object} spaceData - The space data object for animation
     */
    async navigateToSpace(spaceId, spaceData = null) {
        this.ensureDivisionFilterForNavigation({spaceId});
        await nextTick();

        if (getActiveEntityLocatorNavTraceId()) {
            logEntityLocatorNav("locationAnimation.navigateToSpace.start", {
                spaceId,
                hasSpaceData: Boolean(spaceData),
                activePanel: this.workspaceStore.active_panel,
            });
        }

        this.appStateStore.is_dashboard_mode = false;
        // Routing handled by EntityNavigator or calling context
        this.workspaceStore.setIsFetchingItem(true);

        const space = this.workspaceTreeStore.getWorkspaceById(spaceId);
        this.workspaceTreeStore.setActiveWorkspaceId(space?.id, true);

        if (this.workspaceStore.active_panel !== "spaces") {
            this.workspaceStore.openSpacesPanel();
        }
        await nextTick();

        if (spaceData) {
            await this.handleLocationAnimation("space", spaceData);
        } else {
            await this.handleLocationAnimation("space", space);
        }

        if (getActiveEntityLocatorNavTraceId()) {
            logEntityLocatorNav("locationAnimation.navigateToSpace.done", {
                spaceId: space?.id,
                locatorTemp: this.workspaceStore.active_item_locator_temp,
                locator: this.workspaceStore.active_item_locator,
            });
            endEntityLocatorNavTrace({
                status: "success",
                entityType: "space",
                spaceId: space?.id,
            });
        }

        this.workspaceStore.setIsFetchingItem(false);
    }

    /**
     * Navigates to an item group with full business logic
     * @param {number} groupId - The item group ID to navigate to
     * @param {number} boardId - The board ID containing the group
     * @param {number} spaceId - The space ID containing the board
     * @param {Object} groupData - The group data object for animation
     */
    async navigateToItemGroup(groupId, boardId, spaceId, groupData = null) {
        this.ensureDivisionFilterForNavigation({spaceId});

        this.appStateStore.is_dashboard_mode = false;
        // Routing handled by EntityNavigator or calling context
        this.workspaceStore.setIsFetchingItem(true);

        const space = this.workspaceTreeStore.getWorkspaceById(spaceId);
        this.workspaceTreeStore.setActiveWorkspaceId(space?.id, false);

        this.appStateStore.startRestoringState();
        await this.workspaceTreeStore.loadBoards(space);
        const board = this.workspaceTreeStore.getBoardById(boardId);
        this.workspaceTreeStore.setActiveBoardId(board?.id, false);

        const boardRow = this.workspaceTreeStore.getBoardById(board.id);
        if (!boardRow?.itemCategoryOrderedIds?.length) {
            await this.workspaceTreeStore.loadItems(space, board);
        }

        const data = this.workspaceTreeStore.getItemCategoryById(groupId);
        this.workspaceTreeStore.setActiveItemId(null, false);
        this.workspaceTreeStore.setActiveItemCategoryId(groupId, true);
        this.workspaceStore.openItemsPanel();

        if (groupData) {
            await this.handleLocationAnimation("group", groupData, {
                groupType: "item",
            });
        } else {
            await this.handleLocationAnimation("group", data, {groupType: "item"});
        }

        this.workspaceStore.setIsFetchingItem(false);
        this.appStateStore.stopRestoringState();
    }

    /**
     * Navigates to a board group with full business logic
     * @param {number} groupId - The board group ID to navigate to
     * @param {number} spaceId - The space ID containing the group
     * @param {Object} groupData - The group data object for animation
     */
    async navigateToBoardGroup(groupId, spaceId, groupData = null) {
        this.ensureDivisionFilterForNavigation({spaceId});

        this.appStateStore.is_dashboard_mode = false;
        // Routing handled by EntityNavigator or calling context
        this.workspaceStore.setIsFetchingItem(true);

        const space = this.workspaceTreeStore.getWorkspaceById(spaceId);
        this.workspaceTreeStore.setActiveWorkspaceId(space?.id, false);

        this.appStateStore.startRestoringState();
        await this.workspaceTreeStore.loadBoards(space);

        const data = this.workspaceTreeStore.getBoardCategoryById(groupId);

        this.workspaceTreeStore.setActiveBoardId(null, false);
        this.workspaceTreeStore.setActiveBoardCategoryId(groupId, true);
        this.workspaceStore.openBoardsPanel();

        if (groupData) {
            await this.handleLocationAnimation("group", groupData, {
                groupType: "board",
            });
        } else {
            await this.handleLocationAnimation("group", data, {groupType: "board"});
        }

        this.workspaceStore.setIsFetchingItem(false);
        this.appStateStore.stopRestoringState();
    }

    /**
     * Navigates to a space group with full business logic
     * @param {number} groupId - The space group ID to navigate to
     * @param {Object} groupData - The group data object for animation
     */
    async navigateToSpaceGroup(groupId, groupData = null) {
        this.ensureDivisionFilterForNavigation({groupId});
        await nextTick();

        this.appStateStore.is_dashboard_mode = false;
        this.appStateStore.setWorkAsSelectedModule();
        // Routing handled by EntityNavigator or calling context
        this.workspaceStore.setIsFetchingItem(true);

        this.appStateStore.startRestoringState();
        const data = this.workspaceTreeStore.getWorkspaceCategoryById(groupId);
        this.workspaceTreeStore.setActiveWorkspaceCategoryId(groupId, false);
        this.workspaceTreeStore.setActiveWorkspaceId(null, true);
        this.workspaceStore.openSpacesPanel();

        if (groupData) {
            await this.handleLocationAnimation("group", groupData, {
                groupType: "space",
            });
        } else {
            await this.handleLocationAnimation("group", data, {groupType: "space"});
        }

        this.workspaceStore.setIsFetchingItem(false);
        this.appStateStore.stopRestoringState();
    }

    /**
     * Generic navigation method that can be used by any caller
     * @param {string} type - The type of entity to navigate to
     * @param {Object} params - Parameters object containing required data
     * @param {Function} openConversationMappedItem - Optional callback for opening conversation mapped item
     */
    async navigateToEntity(type, params, openConversationMappedItem = null) {
        this.workspaceStore.active_item_locator_cancel = false;

        const navigationMap = {
            item: () =>
                this.navigateToItem(
                    params.itemId,
                    params.spaceId,
                    params.itemData,
                    openConversationMappedItem,
                    params.boardId,
                ),
            board: () =>
                this.navigateToBoard(
                    params.boardId,
                    params.spaceId,
                    params.boardData,
                ),
            space: () => this.navigateToSpace(params.spaceId, params.spaceData),
            "item-group": () =>
                this.navigateToItemGroup(
                    params.groupId,
                    params.boardId,
                    params.spaceId,
                    params.groupData,
                ),
            "board-group": () =>
                this.navigateToBoardGroup(
                    params.groupId,
                    params.spaceId,
                    params.groupData,
                ),
            "space-group": () =>
                this.navigateToSpaceGroup(params.groupId, params.groupData),
        };

        const navigationFunction = navigationMap[type];
        if (navigationFunction) {
            await navigationFunction();
        } else {
            console.warn(`Unknown navigation type: ${type}`);
        }
    }

    /**
     * Handles location animation for different entity types
     * @param {string} type - The type of entity (item, board, space, group)
     * @param {Object} entity - The entity object
     * @param {Object} additionalData - Additional data for group types
     */
    async handleLocationAnimation(type, entity, additionalData = null) {
        const animationMap = {
            item: () => {
                this.setLocateClassName("item", entity);
                return this.redirectToActiveItem();
            },
            board: () => {
                this.setLocateClassName("board", entity);
                return this.redirectToActiveBoard();
            },
            space: () => {
                this.setLocateClassName("space", entity);
                return this.redirectToActiveSpace();
            },
            group: () => {
                this.setLocateClassName("group", entity);
                if (additionalData?.groupType === "board") {
                    return this.redirectToActiveBoardGroup();
                }
                if (additionalData?.groupType === "space") {
                    return this.redirectToActiveSpaceGroup();
                }
                if (additionalData?.groupType === "item") {
                    return this.redirectToActiveItem();
                }

                return Promise.resolve();
            },
        };

        const animationFunction = animationMap[type];
        if (animationFunction) {
            await animationFunction();
        } else {
            console.warn(`Unknown location animation type: ${type}`);
        }
    }
}

/**
 * Factory function to create a LocationAnimation instance
 * @returns {LocationAnimation} - New LocationAnimation instance
 */
export function createLocationAnimation() {
    return new LocationAnimation();
}

export default LocationAnimation;
